import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthCredentials } from "../../../dataTypes/Auth";
import { setSessionData } from "../../../utils/SessionStorage";
import FormErrorSuccessMsg from "../../shared/formErrorSuccessMsg/FormErrorSuccessMsg";
import { AuthContext } from "../context/Context";
import useFetchThemeConfig from "../../../hooks/useFetchThemeConfig";

interface LoginProps {
  themeConfig: {
    color: string;
    logo_img: string;
    favicon_img: string | null;
  };
}

const Login: React.FC<LoginProps> = ({ themeConfig }) => {
  const { login } = useContext(AuthContext);

  const [showPassword, setShowPassword] = useState(false);
  const [payload, setPayload] = useState<AuthCredentials>({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/${slug}/auth/login?adminOnly=true`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        setError("Email e/ou senha inválidos");
      } else {
        const data = await response.json();
        setSessionData({ token: data.data.access_token, user: data.data.user });
        login();

        if (data.data.user.vendor_id) {
          navigate(`/${slug}/${data.data.user.vendor_id}/benefits`);
        } else {
          navigate(`/${slug}/vendors`);
        }

        setPayload({ email: "", password: "" });
        setError(null);
      }
    } catch (error) {
      console.error("Error", error);
      setError("Email e/ou senha inválidos");
      setPayload({ email: "", password: "" });
    } finally {
      setLoading(false);
      setPayload({ email: "", password: "" });
    }
  };

  return (
    <Box className="auth">
      <Box width="100%">
        <Box display="flex" alignItems="center" justifyContent="center">
          <img src={themeConfig.logo_img} alt="Logo" />
        </Box>
        {error && <FormErrorSuccessMsg message={error} success={false} />}
        <form onSubmit={onSubmit}>
          <Box className="form-group">
            <label htmlFor="email" style={{ fontWeight: 700 }}>
              Email
            </label>
            <TextField
              id="outlined-basic-email"
              name="email"
              value={payload.email}
              onChange={handleInputChange}
              variant="outlined"
              type="email"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  height: "40px",
                  fontSize: "0.875rem",
                  borderRadius: "8px",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "10px 14px",
                  borderRadius: "8px",
                },
              }}
            />
          </Box>
          <Box className="form-group">
            <label htmlFor="password" style={{ fontWeight: 700 }}>
              Senha
            </label>
            <TextField
              id="outlined-basic-password"
              name="password"
              value={payload.password}
              onChange={handleInputChange}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  height: "40px",
                  fontSize: "0.875rem",
                  borderRadius: "8px",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "10px 14px",
                  borderRadius: "8px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" justifyContent="end">
            <Link
              color={"#000000"}
              sx={{ cursor: "pointer" }}
              onClick={() => navigate(`/${slug}/forgot-password`)}
            >
              Esqueci minha senha
            </Link>
          </Box>
          <Button
            type="submit"
            variant="contained"
            sx={{
              bgcolor: themeConfig.color,
              marginTop: "16px",
              fontWeight: 700,
              textTransform: "capitalize",
              fontSize: "18px",
              borderRadius: "8px",
            }}
            fullWidth
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Entrar"
            )}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default Login;
